import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOrders } from "@/hooks/useOrders";
import {
  formatOre,
  formatOrderDate,
  STATUS_LABEL,
  FILTER_STATUSES,
  type OrderStatus,
} from "@/lib/orders";

const STATUS_FILTER_OPTIONS = ["all", ...FILTER_STATUSES] as const;
type StatusFilter = (typeof STATUS_FILTER_OPTIONS)[number];

function statusBadge(status: OrderStatus) {
  const label = STATUS_LABEL[status];
  switch (status) {
    case "PAID":
      return <Badge className="bg-blue-500/15 text-blue-700 hover:bg-blue-500/20">{label}</Badge>;
    case "SHIPPED":
      return <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/20">{label}</Badge>;
    case "DELIVERED":
      return <Badge className="bg-green-500/15 text-green-700 hover:bg-green-500/20">{label}</Badge>;
    case "CANCELLED":
    case "REFUNDED":
      return <Badge variant="secondary">{label}</Badge>;
    default:
      return <Badge variant="outline">{label}</Badge>;
  }
}

const AdminOrders = () => {
  const [filter, setFilter] = useState<StatusFilter>("all");
  const status = filter === "all" ? undefined : filter;
  const { data: orders, isLoading, error } = useOrders(status);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Ordrer</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Se betalte bestillinger og oppdater status til sendt eller levert.
          </p>
        </div>
        <div className="w-48">
          <Select value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle statuser</SelectItem>
              {FILTER_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laster …</p>
      ) : error ? (
        <p className="text-sm text-destructive">Kunne ikke laste ordrer.</p>
      ) : !orders || orders.length === 0 ? (
        <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
          Ingen ordrer å vise.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full">
            <thead className="border-b bg-muted/30">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ordre</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Kunde</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Beløp</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Mottatt</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-muted/20">
                  <td className="px-4 py-3 font-mono text-xs">
                    <Link to={`/admin/orders/${order.id}`} className="hover:underline">
                      {order.kco_order_id ?? order.id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium">{order.customer_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{order.customer_email ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{formatOre(order.amount_total_ore)}</td>
                  <td className="px-4 py-3">{statusBadge(order.status)}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                    {formatOrderDate(order.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/orders/${order.id}`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      Åpne
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
